import React, { useState, useEffect } from 'react';
import { fixturesState } from '../../fixtures';
import CheckIcon from '../Icons/Check.icon';
import { Pagination } from '../Pagination';
import { ConfirmacionAprobacionCups, FeedbackModal } from '../Modal';
import './gestionCupsModal.css';
import { FaSearch } from 'react-icons/fa';

interface Columna {
  Atributo: string;
  Nombre: string;
  Orden: number;
}

interface Valor {
  Atributo: string;
  Valor: string;
}

interface Cup {
  Estado: string;
  Id: number;
  Valores: Valor[];
}

interface AcuerdosTarifariosCupsResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    Columnas: Columna[];
    Cups: Cup[];
    TotalAprobados: number;
    TotalCups: number;
    TotalPendientes: number;
    TotalRechazados: number;
  };
}

interface GestionCupsModalProps {
  servicioId: number;
  servicioNombre: string;
  onClose: () => void;
  // Props para manejo de confirmación
  step?: 'gestion' | 'confirmacion-aprobacion' | 'confirmacion-rechazo' | 'feedback';
  cupsParaAprobar?: {id: number; cups: string; descripcion: string}[];
  onShowConfirmation?: (cups: {id: number; cups: string; descripcion: string}[]) => void;
  onShowRechazo?: (cups: {id: number; cups: string; descripcion: string}[]) => void;
  onCancelarConfirmacion?: () => void;
  onConfirmarAprobacion?: () => void;
  onConfirmarRechazo?: (motivo: string) => void;
  // Callback para reportar totales al componente padre
  onTotalesChange?: (totales: { totalCups: number; totalAprobados: number; totalPendientes: number; totalRechazados: number }) => void;
  // Props para feedback
  feedbackTipo?: 'exito' | 'error';
  feedbackMensaje?: string;
  onCerrarFeedback?: () => void;
  // Prop para limpiar selección después de operaciones
  limpiarSeleccion?: boolean;
  // Prop para notificar estado de procesando
  onProcesandoChange?: (procesando: boolean) => void;
}

const CUPS_PAGINATION_SIZE = (() => {
  const envValue = Number(import.meta.env.VITE_ANEXOS_CUPS_PAGE_SIZE || 800);
  return Number.isFinite(envValue) && envValue > 0 ? Math.floor(envValue) : 800;
})();

const GestionCupsModal: React.FC<GestionCupsModalProps> = ({ 
  servicioId, 
  servicioNombre, 
  onClose,
  step = 'gestion',
  cupsParaAprobar = [],
  onShowConfirmation,
  onShowRechazo,
  onCancelarConfirmacion,
  onConfirmarAprobacion,
  onConfirmarRechazo,
  feedbackTipo = 'exito',
  feedbackMensaje = '',
  onCerrarFeedback,
  limpiarSeleccion = false,
  onTotalesChange,
  onProcesandoChange
}) => {
  const [cups, setCups] = useState<Cup[]>([]);
  const [columnas, setColumnas] = useState<Columna[]>([]);
  const [cupsSeleccionados, setCupsSeleccionados] = useState<number[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(false);
  const [procesandoConfirmacion, setProcesandoConfirmacion] = useState(false);
  const [totales, setTotales] = useState({
    totalCups: 0,
    totalAprobados: 0,
    totalPendientes: 0,
    totalRechazados: 0
  });
  
  // Estados para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const cupsPorPagina = 5;

// Función auxiliar para obtener el valor de una columna para un cup específico
  const obtenerValorColumna = (cup: Cup, atributo: string): string => {
    console.log(`Buscando atributo "${atributo}" en:`, cup.Valores);
    const valor = cup.Valores.find(v => v.Atributo === atributo);
    console.log(`Resultado para "${atributo}":`, valor);
    return valor ? valor.Valor : '';
  };

  // Filtrar solo CUPS pendientes
  const normalizarTexto = (texto: string = '') =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const cupsPendientesSolo = cups
  .filter(cup => cup.Estado === 'Pendiente')
  .filter(cup => {
    const textoBusqueda = normalizarTexto(busqueda.trim());

    if (!textoBusqueda) return true;

    const cupsValue =
      obtenerValorColumna(cup, 'Cups') ||
      obtenerValorColumna(cup, 'CUPS') ||
      '';

    const descripcionValue =
      obtenerValorColumna(cup, 'DESCRIPCIÓN') ||
      obtenerValorColumna(cup, 'Descripcion') ||
      obtenerValorColumna(cup, 'Descripción') ||
      obtenerValorColumna(cup, 'DESCRIPCION') ||
      obtenerValorColumna(cup, 'descripcion') ||
      '';

    return (
      normalizarTexto(cupsValue).includes(textoBusqueda) ||
      normalizarTexto(descripcionValue).includes(textoBusqueda)
    );
  });
  
  // Calcular paginación solo para CUPS pendientes
  const totalPaginas = Math.ceil(cupsPendientesSolo.length / cupsPorPagina);
  const indiceInicio = (paginaActual - 1) * cupsPorPagina;
  const indiceFin = indiceInicio + cupsPorPagina;
  const cupsPaginados = cupsPendientesSolo.slice(indiceInicio, indiceFin);

  const cargarCupsData = React.useCallback(async () => {
    if (!servicioId) return;
    
    setLoading(true);
    try {
      // En desarrollo usamos datos mock, en producción consumimos el servicio real
      if (__DEV__ || import.meta.env.MODE === 'development') {
        console.log('Ambiente de desarrollo - Usando datos dinámicos para CUPS tarifarios');
        
        // Simular delay de carga
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const data: AcuerdosTarifariosCupsResponse = fixturesState.getCupsData();
        
        setCups(data.Result.Cups);
        setColumnas(data.Result.Columnas.sort((a, b) => a.Orden - b.Orden));
        
        // Log de depuración
        console.log('Fixture - Columnas cargadas:', data.Result.Columnas);
        console.log('Fixture - CUPS cargados:', data.Result.Cups);
        
        setTotales({
          totalCups: data.Result.TotalCups,
          totalAprobados: data.Result.TotalAprobados,
          totalPendientes: data.Result.TotalPendientes,
          totalRechazados: data.Result.TotalRechazados
        });
      } else {
        // En producción, consumir el servicio real
        console.log('Ambiente de producción - Consumiendo servicio real');
        
        const response = await fetch(
          `/_vti_bin/Servicios/ServicioTransar.svc/ObtenerCupsTarifarios?servicioId=${servicioId}&pagination=${CUPS_PAGINATION_SIZE}`
        );
        const data: AcuerdosTarifariosCupsResponse = await response.json();
        
        if (data.ResponseCode === 200) {
          setCups(data.Result.Cups);
          setColumnas(data.Result.Columnas.sort((a, b) => a.Orden - b.Orden));
          setTotales({
            totalCups: data.Result.TotalCups,
            totalAprobados: data.Result.TotalAprobados,
            totalPendientes: data.Result.TotalPendientes,
            totalRechazados: data.Result.TotalRechazados
          });
        } else {
          console.error('Error al cargar CUPS:', data.ResponseDetail);
          setCups([]);
          setColumnas([]);
        }
      }
    } catch (error) {
      console.error('Error al cargar CUPS:', error);
      setCups([]);
      setColumnas([]);
    } finally {
      setLoading(false);
    }
  }, [servicioId]);

  useEffect(() => {
    cargarCupsData();
    // Resetear página al cambiar servicio
    setPaginaActual(1);
  }, [cargarCupsData]);

  // Limpiar selección cuando se indica
  useEffect(() => {
    if (limpiarSeleccion) {
      setCupsSeleccionados([]);
    }
  }, [limpiarSeleccion]);

  // Notificar al componente padre cuando cambien los totales
  useEffect(() => {
    if (onTotalesChange) {
      onTotalesChange(totales);
    }
  }, [totales, onTotalesChange]);

  // Notificar al componente padre cuando cambie el estado de procesando
  useEffect(() => {
    if (onProcesandoChange) {
      onProcesandoChange(procesandoConfirmacion);
    }
  }, [procesandoConfirmacion, onProcesandoChange]);

  

  const handleCupSeleccionado = (cupId: number, isSelected: boolean) => {
    if (isSelected) {
      setCupsSeleccionados([...cupsSeleccionados, cupId]);
    } else {
      setCupsSeleccionados(cupsSeleccionados.filter(id => id !== cupId));
    }
  };

  const seleccionarTodos = (isSelected: boolean) => {
    if (isSelected) {
      // Seleccionar TODOS los CUPS pendientes (usando la lista filtrada)
      const todosCupsPendientes = cupsPendientesSolo.map(cup => cup.Id);
      setCupsSeleccionados(todosCupsPendientes);
    } else {
      // Deseleccionar todos
      setCupsSeleccionados([]);
    }
  };

  const aprobarSeleccionados = () => {
    if (cupsSeleccionados.length === 0) {
      return;
    }
    
    // Preparar la lista de CUPS seleccionados para mostrar en el modal
    const cupsParaConfirmar = cupsSeleccionados.map(id => {
      const cup = cups.find(c => c.Id === id);
      if (!cup) return null;
      
      // Buscar valores para cups y descripción usando la estructura dinámica
      const cupsValue = obtenerValorColumna(cup, 'Cups') || obtenerValorColumna(cup, 'CUPS') || '';
      const descripcionValue = obtenerValorColumna(cup, 'DESCRIPCIÓN') || 
                             obtenerValorColumna(cup, 'Descripcion') || 
                             obtenerValorColumna(cup, 'Descripción') || 
                             obtenerValorColumna(cup, 'DESCRIPCION') || 
                             obtenerValorColumna(cup, 'descripcion') || '';
      
      console.log(`CUPS ID ${cup.Id} (aprobar):`, { cupsValue, descripcionValue, valores: cup.Valores });
      
      return { 
        id: cup.Id, 
        cups: cupsValue, 
        descripcion: descripcionValue 
      };
    }).filter(Boolean) as { id: number; cups: string; descripcion: string }[];
    
    if (cupsParaConfirmar.length === 0) {
      return;
    }
    
    // Comunicar al padre que se necesita mostrar confirmación
    if (onShowConfirmation) {
      onShowConfirmation(cupsParaConfirmar);
    }
  };

  // Funciones de paginación
  const irAPagina = (numeroPagina: number) => {
    if (numeroPagina >= 1 && numeroPagina <= totalPaginas) {
      setPaginaActual(numeroPagina);
    }
  };

  const paginaAnterior = () => {
    if (paginaActual > 1) {
      setPaginaActual(paginaActual - 1);
    }
  };

  const paginaSiguiente = () => {
    if (paginaActual < totalPaginas) {
      setPaginaActual(paginaActual + 1);
    }
  };

  // Verificar si todos los CUPS pendientes están seleccionados (usando la lista filtrada)
  const todosCupsSeleccionados = cupsPendientesSolo.length > 0 && cupsPendientesSolo.every(cup => cupsSeleccionados.includes(cup.Id));

  const rechazarSeleccionados = () => {
    // Preparar los CUPS seleccionados para rechazo
    const cupsParaRechazar = cupsSeleccionados.map(id => {
      const cup = cups.find(c => c.Id === id);
      if (!cup) return null;
      
      // Buscar valores para cups y descripción usando la estructura dinámica
      const cupsValue = obtenerValorColumna(cup, 'Cups') || obtenerValorColumna(cup, 'CUPS') || '';
      const descripcionValue = obtenerValorColumna(cup, 'DESCRIPCIÓN') || 
                             obtenerValorColumna(cup, 'Descripcion') || 
                             obtenerValorColumna(cup, 'Descripción') || 
                             obtenerValorColumna(cup, 'DESCRIPCION') || 
                             obtenerValorColumna(cup, 'descripcion') || '';
      
      console.log(`CUPS ID ${cup.Id} (rechazar):`, { cupsValue, descripcionValue, valores: cup.Valores });
      
      return { 
        id: cup.Id, 
        cups: cupsValue, 
        descripcion: descripcionValue 
      };
    }).filter(Boolean) as { id: number; cups: string; descripcion: string }[];
    
    if (cupsParaRechazar.length === 0) {
      return;
    }
    
    // Comunicar al padre que se necesita mostrar rechazo
    if (onShowRechazo) {
      onShowRechazo(cupsParaRechazar);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Contenido principal de gestión de CUPS
  const getGestionContent = () => {
    console.log('Renderizando tabla - Columnas disponibles:', columnas);
    console.log('Renderizando tabla - CUPS disponibles:', cups.length);
    
    return (
      <div className="gestion-cups-modal-content">
        {/* Header con descripción y totales en la misma línea */}
        <div className="modal-header">
          <div className="header-left">
            <p className="modal-description">
              Selecciona los CUPS que deseas aprobar en grupos o rechazar de forma individual.
            </p>
          </div>
          <div className="header-search">
            
            <div className="search-input-container">
              <input
                type="text"
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPaginaActual(1);
                }}
                placeholder="Buscar"
                className="search-input"
                title="Buscar"
              />

              <span className="search-icon"
                    title="Buscar"
              >
                {FaSearch({})}
              </span>
            </div>
          </div>
          <div className="header-right">
            <div className="totales-info">
              <span className="total-item">
                <span className="total-label">CUPS encontrados:</span>
                <span className="total-value">{cupsPendientesSolo.length}</span>
              </span>
              <span className="total-item aprobados">
                <span className="total-label">Aprobados:</span>
                <span className="total-value">{totales.totalAprobados}</span>
              </span>
              <span className="total-item rechazados">
                <span className="total-label">Rechazados:</span>
                <span className="total-value">{totales.totalRechazados}</span>
              </span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <p>Cargando CUPS...</p>
          </div>
        ) : cups.length > 0 ? (
        <div className="modal-body">
          {/* Contenedor scrolleable solo para la tabla */}
          <div className="cups-table-container">
            {columnas.length > 0 ? (
              <table className="cups-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>
                      <div className="checkbox-header-container">
                        <div className="checkbox-label">Todos</div>
                        <input
                          type="checkbox"
                          checked={todosCupsSeleccionados}
                          onChange={(e) => seleccionarTodos(e.target.checked)}
                          className="checkbox-common"
                        />
                      </div>
                    </th>
                    {columnas.map((columna) => (
                      <th key={columna.Atributo}>{columna.Nombre}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cupsPaginados.length > 0 ? (
                    cupsPaginados.map((cup) => (
                      <tr key={cup.Id}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={cupsSeleccionados.includes(cup.Id)}
                            onChange={(e) => handleCupSeleccionado(cup.Id, e.target.checked)}
                            className="checkbox-common"
                          />
                        </td>

                        {columnas.map((columna) => {
                          const valor = obtenerValorColumna(cup, columna.Atributo);

                          // Log de depuración para el primer cup
                          if (cup.Id === cups[0]?.Id) {
                            console.log(`Columna: ${columna.Atributo}, Valor: "${valor}", Cup:`, cup);
                          }

                          const tieneFormatoMonetario = (val: string): boolean => {
                            const patronMonetario = /^[\d.,]+$/;
                            const tieneComaDecimal = /\d+,\d{2}$/;
                            const tienePuntoDecimal = /\d+\.\d{2}$/;

                            return patronMonetario.test(val) && (tieneComaDecimal.test(val) || tienePuntoDecimal.test(val));
                          };

                          const normalizarValorMonetario = (val: string): number => {
                            const valorNormalizado = val.replace(/\./g, '').replace(',', '.');
                            return Number(valorNormalizado);
                          };

                          const esColumnaMonetariaPorNombre = /tarifa|precio|costo|valor|honorario|pago/i.test(columna.Nombre);
                          const esColumnaMonetariaPorFormato = tieneFormatoMonetario(valor);
                          const esColumnaMonetaria = esColumnaMonetariaPorNombre || esColumnaMonetariaPorFormato;

                          const valorNumerico = esColumnaMonetaria && esColumnaMonetariaPorFormato
                            ? normalizarValorMonetario(valor)
                            : Number(valor);

                          const esNumerico = !isNaN(valorNumerico) && valor !== '' && valor !== '0';

                          return (
                            <td
                              key={columna.Atributo}
                              style={{
                                textAlign: (esColumnaMonetaria && esNumerico) ? 'right' : 'left'
                              }}
                            >
                              {esColumnaMonetaria && esNumerico
                                ? formatCurrency(valorNumerico)
                                : valor}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columnas.length + 1}
                        style={{
                          textAlign: 'center',
                          padding: '32px',
                          color: '#666'
                        }}
                      >
                        No se encontraron CUPS con ese criterio de búsqueda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                Cargando columnas...
              </div>
            )}
          </div>

          {/* Footer fijo con paginación y botones */}
          <div className="modal-footer">
            {/* Paginación */}
            <Pagination
              paginaActual={paginaActual}
              totalPaginas={totalPaginas}
              onPaginaAnterior={paginaAnterior}
              onPaginaSiguiente={paginaSiguiente}
              onIrAPagina={irAPagina}
            />

            {/* Botones de acción */}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-modal-rechazar"
                onClick={rechazarSeleccionados}
                disabled={cupsSeleccionados.length === 0}
              >
                <span className="icon-close"></span>
                Rechazar
              </button>
              <button
                type="button"
                className="btn-modal-aprobar"
                onClick={aprobarSeleccionados}
                disabled={cupsSeleccionados.length === 0}
              >
                <CheckIcon style={{ width: '16px', height: '16px' }} />
                {`Aprobar${cupsSeleccionados.length > 0 ? ` (${cupsSeleccionados.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
        ) : (
          <div className="no-data-container">
            <p>No se encontraron CUPS para este servicio.</p>
          </div>
        )}
      </div>
    );
  };

  // Renderizar según el paso
  if (step === 'confirmacion-aprobacion') {
    return (
      <ConfirmacionAprobacionCups
        cups={cupsParaAprobar}
        tipo="aprobacion"
        onCancelar={onCancelarConfirmacion || onClose}
        onConfirmarAprobacion={async () => {
          if (onConfirmarAprobacion) {
            await onConfirmarAprobacion();
          } else {
            onClose();
          }
        }}
        onProcesandoChange={(procesando) => setProcesandoConfirmacion(procesando)}
      />
    );
  }

  if (step === 'confirmacion-rechazo') {
    return (
      <ConfirmacionAprobacionCups
        cups={cupsParaAprobar}
        tipo="rechazo"
        onCancelar={onCancelarConfirmacion || onClose}
        onConfirmarRechazo={async (motivo: string) => {
          if (onConfirmarRechazo) {
            await onConfirmarRechazo(motivo);
          } else {
            onClose();
          }
        }}
        onProcesandoChange={(procesando) => setProcesandoConfirmacion(procesando)}
      />
    );
  }

  if (step === 'feedback') {
    return (
      <FeedbackModal
        tipo={feedbackTipo}
        mensaje={feedbackMensaje}
        onClose={onCerrarFeedback || onClose}
      />
    );
  }

  return getGestionContent();
};

export default GestionCupsModal;